// import { ComponentNames } from "src/services/ContentProvider/ContentProviderProps";
import { ComponentNames } from "../../constant";
import ReportingService from "../../../src/services/ReportingService";
import ChannelLineUpProvider, {
  Channels,
  KeyValuePair,
  SortOption
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  parseThemeObject,
  defaultThemingDark
} from "../../helper/channel-lineup-theme";
import {
  parseChannelsData,
  parsePackagesData
} from "../../helper/channel-lineup-parser";
import { addPackageDetailsToEmbeddedSvods } from "../../helper/svod-utils";
import { IEmbeddedSvodOffers } from "../../types/global";
import { addGenrePackages } from "../../../src/utils/genreUtility";
import { logAdapterEvent, logError } from "../../helper/logger";
import { getGenreMapping, getGenreToPackageMapping } from "../../helper/genre-helper";
import { pickPlatform, platformOf, pickChannels, pickChrome, isUsingServiceChannels } from "../../helper/channels-service-helpers";

const ADAPTER_NAME = "ChannelLineupPageAdapter";

const formatSvodArrayToString = (arr: string[]): string => {
  if (arr?.length === 0) {
    return "";
  }
  if (arr?.length === 1) {
    return arr?.[0];
  }
  if (arr?.length === 2) {
    return `${arr?.[0]} and ${arr?.[1]}`;
  }

  const lastItem = arr?.pop();
  return `${arr?.join(", ")} and ${lastItem}`;
};

interface IChannelLineUpPageAdapter {
  content: any;
  componentId: string;
  index: number;
  queryParams?: any; // to-do remove
  myFreeDTVConfig?: any;
  mySportsEligibleCookie?: any;
  genreLocalChannelMapping?: any;
  genreOffers?: any;
  svodGenreData?: any;
  enableGenrePackChannelView?: any;
  packageConfig?: any;
  embeddedSvods?: any;
  dtviEmbeddedSvodOffers?: IEmbeddedSvodOffers;
  dtvsEmbeddedSvodOffers?: IEmbeddedSvodOffers;
  isEmbeddedSvodsEnabled?: boolean;
  genreNationals?: any;
  rsnTierLegalTextObj?: any;
  isSatelliteChlup?: any;
  /**
   * channels-service v4 envelope `{ platform, lastUpdated, channels[] }` for the
   * platform this adapter renders. When provided, channels[] come from here
   * instead of `content.channels` (the AEM path); both feed the same parser.
   */
  channelsServiceData?: any;
  /**
   * Raw AEM channel array (`elements.channels`) used as the degraded fallback
   * when the channels-service is enabled but empty/failed. Flag ON, `content`
   * carries no channels (channels never live in the SB story), so this — not
   * `content.channels` — is the real AEM fallback. Same shape parseChannelsData
   * consumes flag OFF.
   */
  aemChannels?: any;
  /**
   * Raw AEM `channelCategories` (`elements.channelCategories`, slug-keyed
   * "Label,KEY" options). Category bucketing follows the channel source: prose
   * package-config when the service supplies channels; this AEM config (production
   * behavior) when channels come from AEM (flag-OFF/degraded) so the categoryId
   * slug matches.
   */
  aemChannelCategories?: any;
  /**
   * Chrome/CCT config from the global package-config feed (assembled in
   * page-adapter). Each field is preferred over the per-page `content.*` value;
   * absent fields fall back to `content.*` so the flag-OFF (AEM) path is unchanged.
   */
  globalLineupConfig?: any;
}

export const ChannelLineUpPageAdapter = ({
  content,
  componentId,
  index,
  queryParams: _queryParams, // to-do remove
  genreLocalChannelMapping: _genreLocalChannelMapping,
  packageConfig,
  enableGenrePackChannelView,
  svodGenreData,
  genreOffers,
  embeddedSvods: embeddedSvodsArg,
  dtviEmbeddedSvodOffers,
  dtvsEmbeddedSvodOffers,
  isEmbeddedSvodsEnabled: isEmbeddedSvodsEnabledArg,
  genreNationals,
  rsnTierLegalTextObj,
  isSatelliteChlup,
  channelsServiceData,
  aemChannels,
  aemChannelCategories,
  globalLineupConfig
}: IChannelLineUpPageAdapter): any => {
  let embeddedSvods = embeddedSvodsArg;
  let isEmbeddedSvodsEnabled = isEmbeddedSvodsEnabledArg;
  try {
    logAdapterEvent(ADAPTER_NAME, "starting channel lineup page adaptation", {
      componentId,
      index,
      isEmbeddedSvodsEnabled,
      enableGenrePackChannelView
    });

    if (content) {
      // `platform` drives `pickPlatform` below. It uses the SB-story sibling-key
      // vocabulary (`"stream" | "satellite"`), independent of the channels-service
      // REST `Platform` (`"ott" | "satellite" | "business"`). Fields may carry
      // sibling keys `{stream, satellite}`; `pickPlatform` returns the platform's
      // value when present, else the value as-is for flat/AEM content.
      const platform = platformOf(isSatelliteChlup);

      // Chrome/CCT config: each field prefers the global package-config feed and
      // falls back to `content?.*`, keeping the flag-OFF (AEM) path unchanged.
      // `packages` is special — the global feed is an app-wide catalog, so filter
      // to this route's platform prefix (ATT_TV_* stream / DTV_* satellite).
      const platformPrefix = isSatelliteChlup ? "DTV" : "ATT_TV";
      const resolvedPackages = pickChrome(
        globalLineupConfig?.packages?.filter((pkg) => pkg?.packageId?.includes(platformPrefix)),
        content?.packages
      );
      // Category-bucketing config follows the channel SOURCE (Option C): use the
      // prose package-config blok [{label, engCategories[]}] ONLY when the service
      // is actually supplying channels. When channels come from AEM (flag-OFF OR
      // flag-ON degraded), bucket against the AEM channelCategories (slug "Label,KEY"
      // options → getKVsFromStrings below) so the channels' categoryId slug matches
      // — production behavior. Mirrors pickChannels so config + channels never diverge.
      const categoryConfig = isUsingServiceChannels(channelsServiceData?.channels)
        ? globalLineupConfig?.categoryConfig
        : undefined;
      const resolvedChannelCategories = categoryConfig
        ? null
        : (aemChannelCategories ?? content?.channelCategories ?? null);
      const resolvedGenrePackages = pickChrome(globalLineupConfig?.genrePackages, content?.genrePackages);
      const resolvedMiniPacks = pickChrome(globalLineupConfig?.miniPacks, content?.miniPacks);

      const variantPackages = pickPlatform(resolvedPackages, platform);
      const variantHeading = pickPlatform(content?.heading, platform);
      const variantPageHeading = pickPlatform(content?.pageHeading, platform);
      const variantToggleBtn = pickPlatform(content?.toggleBtn, platform);
      const variantTabTrayBtn = pickPlatform(content?.tabTrayBtn, platform);
      const variantInternetChnlsPDFLink = pickPlatform(content?.internetChnlsPDFLink, platform);
      const variantSatelliteChnlsPDFLink = pickPlatform(content?.satelliteChnlsPDFLink, platform);
      const variantByodInternetChnlsPDFLink = pickPlatform(content?.byodInternetChnlsPDFLink, platform);
      const variantInternetPkgCta = pickPlatform(content?.internetPkgCta, platform);
      const variantSatellitePkgCta = pickPlatform(content?.satellitePkgCta, platform);
      const variantSatelliteCta = pickPlatform(content?.satelliteCta, platform);
      const variantByodSatelliteCta = pickPlatform(content?.byodSatelliteCta, platform);
      const variantCtaForSatelliteLocalRSN = pickPlatform(content?.ctaForSatelliteLocalRSN, platform);
      const variantCtaForInternetInLocalRSN = pickPlatform(content?.ctaForInternetInLocalRSN, platform);

      /** --------------------------------------------------------------------------------------
       * Package Config
       */
      if (!variantPackages) {
        throw new Error("content.packages is not defined");
      }
      if (!isSatelliteChlup && !resolvedGenrePackages) {
        throw new Error("content.genrePackages is not defined");
      }

      const signaturePackagesBase = [...variantPackages]?.map(
        (pkg, index) => ({
          ...pkg,
          enabled: true,
          isGenrePack: false,
          isGenreMiniPack: false,
          packageIndicatorIndex: Number(index)
        })
      );

      const genrePackagesBase = !isSatelliteChlup ? addGenrePackages(
        [],
        resolvedGenrePackages,
        packageConfig
      )?.map((pkg, index) => ({
        ...pkg,
        enabled: true,
        isGenrePack: true,
        isGenreMiniPack: resolvedMiniPacks?.options?.some((miniPack: string) => pkg?.packageId?.includes(
          miniPack?.split(",")?.[1]?.trim()?.toUpperCase()
        )),
        packageIndicatorIndex: Number(index)
      })) : null;

      /** --------------------------------------------------------------------------------------
       * Generate dynamic genre mappings from packageConfig
       */
      const genreMapping = getGenreMapping({ packageConfig: packageConfig || [] });
      const genreToPackageMapping = getGenreToPackageMapping({ packageConfig: packageConfig || [] });

      // Channel source: channels-service envelope when present (flag ON). When
      // the service is enabled but empty/failed, fall back to the AEM channels
      // (aemChannels); flag OFF, content.channels already holds the AEM payload.
      // All three feed the same parser.
      const sourceChannels = pickChannels(
        channelsServiceData?.channels,
        aemChannels ?? content?.channels
      );
      if (!sourceChannels?.length) {
        throw new Error(
          "No channel source available — channels-service, AEM fallback, and content.channels are all empty"
        );
      }

      const packageIds = variantPackages.map(
        pack => (pack.categoryId === "ENGLISH" ? pack.packageId : null)
      ).filter(p => p && (isSatelliteChlup ? p.includes("DTV") : p.includes("ATT_TV")));
      const filteredChannels = filterChannels(
        variantPackages,
        sourceChannels,
        resolvedGenrePackages
      );
      const parsedChannels = parseChannelsData(
        content?.chnlSrc,
        filteredChannels,
        packageIds,
        // packageCategories,
        signaturePackagesBase,
        // myFreeDTVConfig,
        genrePackagesBase,
        isSatelliteChlup
      )?.sort(Channels.sortAlphabetically);

      const myFreeDtvChannels = parsedChannels
        ?.filter(channel => channel.isMyFreeDTV)
        ?.map((channel: any) => ({
          channelNameLabel: channel?.label || channel?.name,
          channelLogoPath: channel?.logo || channel?.icon
        }))
        ?.sort((a, b) => {
          if (
            a.channelNameLabel.toLowerCase() > b.channelNameLabel.toLowerCase()
          ) {
            return 1;
          }
          if (
            a.channelNameLabel.toLowerCase() ===
            b.channelNameLabel.toLowerCase()
          ) {
            return 0;
          }
          return -1;
        });

      /** - Package configuration --------------------------------------------------------------
       * Final packages sent to the UI
       */
      const signaturePackages = parsePackagesData(signaturePackagesBase);
      const genrePackages = !isSatelliteChlup ? parsePackagesData(genrePackagesBase) : null;

      /** Signature Categories.
       * Flag ON (blok): KVs derived from CMS config labels — label is both key and
       *   display value; the CMS array order defines accordion order.
       * Flag OFF (AEM CSV): parse existing "Label,KEY" options as before.
       */
      if (!categoryConfig && !resolvedChannelCategories?.options) {
        throw new Error("content.channelCategories is not defined");
      }
      // Flag is computed once; all three derived category sets branch on it
      // together. Flag ON derives from the CMS categoryConfig labels; flag OFF
      // derives from the parsed AEM CSV KVs. Behavior is identical to the prior
      // per-set ternaries.
      let signatureCategoriesKV: KeyValuePair[];
      let sportsCategory: KeyValuePair[];
      let otherCategories: KeyValuePair[];
      if (categoryConfig) {
        // Genre KVs carry engCategories so the CLIENT bucketer (bucketChannelsByCategory)
        // can match channel.categories[] ↔ engCategories. Structural rows (RSN/LOCAL/ADD_ON)
        // stay KV-only — they have no engCategories and are handled by the store's structural loop.
        const engOf = (label: string) =>
          categoryConfig.find((c: any) => c.label === label)?.engCategories;
        signatureCategoriesKV = categoryConfig.map((c: any) => ({ key: c.label, value: c.label }));
        sportsCategory = categoryConfig
          .filter((c: any) => c.engCategories?.some((e: string) => e.toLowerCase().includes("sport")))
          .map((c: any) => ({ key: c.label, value: c.label, engCategories: c.engCategories }));
        otherCategories = signatureCategoriesKV
          ?.filter(ctg =>
            categoryConfig.find((c: any) => c.label === ctg.key)?.engCategories?.length > 0
              && !sportsCategory?.some((s: any) => s.key === ctg.key)
          )
          .map(ctg => ({ ...ctg, engCategories: engOf(ctg.key) }));
      } else {
        signatureCategoriesKV = ChannelLineUpProvider.getKVsFromStrings(resolvedChannelCategories?.options);
        sportsCategory = signatureCategoriesKV?.filter(ctg => ctg.key === "SPORTS");
        otherCategories = signatureCategoriesKV?.filter(ctg =>
          !["LOCAL", "RSN", "SPORTS"].includes(ctg.key)
        );
      }

      // Adding packageListSystem for Embedded & Included SVODs retrieved from epoch getOffers
      embeddedSvods = addPackageDetailsToEmbeddedSvods({
        embeddedSvods,
        epochSvodsData: isSatelliteChlup ? dtvsEmbeddedSvodOffers : dtviEmbeddedSvodOffers,
        packageConfig,
        componentType: ComponentNames.CHANNEL_LISTING_PAGE,
        signaturePackages,
        isSatellite: !!isSatelliteChlup
      });
      // Edge Case, if embeddedSvods length is 0, then set isEmbeddedSvodsEnabled to false to hide Accordions and Unalter BAU functionality
      if (embeddedSvods?.length === 0) {
        isEmbeddedSvodsEnabled = false;
      }

      // RSN / LOCAL / ADD_ON / MyFree are structural rows — left as-is.
      // Genre rows (sportsCategory, otherCategories) are derived from categoryConfig
      // labels when flag-ON, or from AEM CSV when flag-OFF.

      // Zip/DMA-sourced rows (Local channels, Regional Sports) lead, then
      // National Sports and the rest of the categoryConfig categories. ADD_ON /
      // MyFree stay at the tail; INCLUDED_SUBSCRIPTIONS unshifts to the top below.
      // sportsCategory may be [] (no sport-tagged category in CMS); fall back to the
      // structural key "SPORTS" so this row still renders with a valid key.
      const nationalSportsCategory = {
        ...sportsCategory?.[0],
        key: sportsCategory?.[0]?.key ?? "SPORTS",
        value: "National Sports"
      };
      const signatureGridCategoriesKV: any[] = [
        { key: "LOCAL", value: "Local channels"  },
        { key: "RSN",   value: "Regional Sports" },
        nationalSportsCategory,
        ...otherCategories,
        { key: "ADD_ON_CHANNELS", value: "Add on channels" },
        ...(content?.myFreeConfig?.myFreeCategory ? [content?.myFreeConfig?.myFreeCategory] : [])
      ];
      // Add EmbeddedSVODs Accordian Data to Categorical View only if feature is enabled
      if (isEmbeddedSvodsEnabled && embeddedSvods?.length > 0) {
        signatureGridCategoriesKV.unshift({
          key: "INCLUDED_SUBSCRIPTIONS",
          value: "Included subscriptions"
        });
      }

      /** Genre Pack Categories
       * - Get KVs
       * - update genre categories to include SVOD names
       */
      const resolvedGenreChannelCategories =
        pickChrome(globalLineupConfig?.genreChannelCategories, content?.genreChannelCategories);

      if (
        !isSatelliteChlup &&
        (!resolvedGenreChannelCategories?.options ||
        !resolvedMiniPacks?.options)
      ) {
        throw new Error("content.genreChannelCategories is not defined");
      }

      const svodMapping = genreOffers?.svodMapping;

      const genreChannelCategoriesKV = !isSatelliteChlup ? ChannelLineUpProvider.getKVsFromStrings(
        resolvedGenreChannelCategories?.options
      ) : [];
      // MySports, MyEntertainment, MiEspanol, MyNews and Locals, etc
      const miniPackCategoriesKV = ChannelLineUpProvider.getKVsFromStrings(
        resolvedMiniPacks?.options
      );
      // My Cinema, MyKids, MySports Extra, MyHomeTeam, etc

      const genreCategoriesWithSVODLabels: KeyValuePair[] = [];
      genreChannelCategoriesKV?.forEach(({ key, value }) => {
        const genreCatKey = `ATT_TV_${key?.toUpperCase()}`;

        if (svodGenreData) {
          const svodNames = formatSvodArrayToString(
            svodGenreData
              ?.filter(svodPackage => svodMapping?.[genreCatKey?.toUpperCase()]?.includes(
                svodPackage?.productSKU
              ))
              ?.map(filteredSvodPackage => filteredSvodPackage?.name) || []
          );
          const newValue = svodNames ? `${value} (with ${svodNames})` : value;
          if (!miniPackCategoriesKV?.some(pack => pack.key === key)) {
            genreCategoriesWithSVODLabels?.push({
              key,
              value: newValue
            });
          }
        }
      });

      const miniCategories: KeyValuePair[] = [];
      resolvedMiniPacks?.options?.forEach(miniPack => {
        const [name, key] = miniPack.split(",").map(item => item.trim());
        miniCategories?.push({
          key,
          value: name
        });
      });

      const genreAndMiniCategories: KeyValuePair[] = !isSatelliteChlup ? [
        ...(genreCategoriesWithSVODLabels?.length ?
          genreCategoriesWithSVODLabels :
          []),
        ...(miniCategories?.length ? miniCategories : []),
        ...(content?.myFreeConfig?.myFreeCategory ? [content?.myFreeConfig?.myFreeCategory] : [])
      ]?.sort((a, b) => {
        // Simplified version of genreUtility/sortGenrePack
        const orderA = genrePackages?.find(pkg => pkg?.id?.includes(a?.key))
          ?.packageConfig?.order;
        const orderB = genrePackages?.find(pkg => pkg?.id?.includes(b?.key))
          ?.packageConfig?.order;
        return orderA !== undefined && typeof orderB !== 'undefined' ?
          Number(orderA) - Number(orderB) :
          orderA !== undefined ?
            -1 :
            orderB !== undefined ?
              1 :
              0;
      }) : [];

      // `cct` is the source of CCT fields (sortOptions, commonData,
      // localAndRegionalChannels). Shape-aware (not flag-aware): AEM-derived
      // content nests them under `chnlCmpToolData.elements`, a real SB story has
      // them flat — so prefer the nested path and fall back to flat.
      const cct = content?.chnlCmpToolData?.elements ?? content;

      // Sort options for the Pkg Comparison Grid. The SB feed (flag ON) authors
      // these as `array_sortOptions` → normalized to a FLAT `sortOptions` array;
      // the AEM flat file (flag OFF) nests them under
      // `chnlCmpToolData.elements.sortOptions.options` (an object). So `pickChrome`
      // reads the SB array when the flag is on, else the AEM `.options` — per
      // surface, NOT the shared package-config feed (lineup owns its 2; the modal
      // owns the 3rd "Channel Number"). Hardcoded A-Z/Category is the last resort.
      const pSortOptions =
        pickChrome(content?.sortOptions, cct?.sortOptions?.options) || [
          "A-Z,ALPHABETICAL",
          "Category,CATEGORY"
        ];
      const pDfltSortOption =
        pickChrome(content?.defaultSortOptions, cct?.defaultSortOptions) ?? "";
      const sortOptions: SortOption[] =
        ChannelLineUpProvider.getSortOptionsFromStrings(
          pSortOptions,
          pDfltSortOption
        );
      let initViewMode = sortOptions?.[0] || {
        key: 0,
        value: "A-Z",
        label: "Alphabetical",
        default: true
      };
      if (pDfltSortOption) {
        initViewMode =
          sortOptions.find(
            s => s.value.toUpperCase() === pDfltSortOption?.value?.toUpperCase() ||
              s?.default === true
          ) || initViewMode;
      }

      // Analytics data
      const analyticsData = new ReportingService().fixAnalyticsValues(
        {
          ...content,
          ...{ analytics: { Infinity: content.analytics } }
        },
        null,
        ComponentNames.CHANNEL_LISTING_PAGE,
        index
      );

      const fixedContent = {
        props: {
          /** -- START -- Server Generated */
          channelData: {
            // ...content?.channelData
            channels: JSON.parse(JSON.stringify(parsedChannels)),
            bannerCategories: genreAndMiniCategories,
            bannerPackages: genrePackages,
            searchCategories: [...genreAndMiniCategories, ...signatureCategoriesKV],
            gridCategories: signatureGridCategoriesKV,
            gridPackages: signaturePackages?.filter(
              pkg => pkg?.category === "ENGLISH"
            ),
            ...(content?.myFreeDirectvBanner && {
              myFreeDtvChannels
            })
          },
          config: {
            chnlSrc: isSatelliteChlup ? "satelliteclup" : "streamclup", // dtvclup
            analytics: analyticsData.analytics
          },
          /** -- END -- Server Generated */

          /** -- START -- SB Content */
          content: {
            isSatelliteChlup,
            theming: content?.theming ?
              parseThemeObject(
                content?.theming,
                signaturePackages?.filter(
                  pkg => pkg?.category === "ENGLISH"
                )?.length || 4
              ) :
              parseThemeObject(
                defaultThemingDark,
                signaturePackages?.filter(
                  pkg => pkg?.category === "ENGLISH"
                )?.length || 4
              ),
            // @Renuka collapse to content?.genrePackTriggers
            genrePackTriggers: content?.chlupDefaultTab?.genrePackTriggers,
            // @Renuka ensure this is not an array, then remove 'or' clause
            heading: variantHeading?.[0] || variantHeading,
            pageHeading: variantPageHeading?.[0] || variantPageHeading,
            // @Renuka content?.toggleTab can be removed. If switching back to Signature view, CI can change
            // @Renuka index of genre packs should be 1
            /** CHANGED */ toggleBtn: {
              ...variantTabTrayBtn,
              toggles: variantTabTrayBtn?.toggles?.map((toggle, ix) => ({
                ...toggle,
                index: ix
              }))
            },
            subheading: content?.subheading,
            subheadingLegal: content?.localRsnLegal,
            // @Renuka change to target url, to align with DtvButtonProps (keep support for other props)
            zipcodeModuleConfig: content?.zipcodeModuleConfig || {},
            ...(content?.myFreeDirectvBanner?.logo && {
              myFreeDirectvBanner: {
                ...content?.myFreeDirectvBanner,
                modalData: {
                  ...content?.myFreeDirectvBanner?.modalData,
                  cta: {
                    ...content?.myFreeDirectvBanner?.modalData?.cta,
                    targeturl:
                    content?.myFreeDirectvBanner?.modalData?.cta?.url ||
                    "https://www.directv.com/dtvassets/sales/directv/upper_funnel/directv/my-free-directv-cta.pdf"
                  }
                }
              }
            }),
            localChnlBadgeTooltip: {
              content:
                pickChrome(globalLineupConfig?.commonData?.localBadgeTooltip, cct?.localAndRegionalChannels?.localTooltip?.content)
            },
            rsnChnlBadgeTooltip: {
              content:
                pickChrome(globalLineupConfig?.commonData?.rsnBadgeTooltip, cct?.localAndRegionalChannels?.rsnTooltip?.content)
            },
            pkgIndicatorIcons: {
              included:
                "https://www.directv.com/dtvassets/global/icons/component-controls/icons-in-table_check.svg",
              // @Renuka Wrong value at content?.chnlCmpToolData?.elements?.commonData?.channelIncludedLogoPath,
              addon:
                "https://www.directv.com/dtvassets/global/icons/component-controls/icons-in-table_dollar.svg",
              // @Renuka Wrong value at from -- content?.chnlCmpToolData?.elements?.commonData?.channelAddOnLogoPath,
              promo:
                "https://www.directv.com/dtvassets/global/icons/component-controls/icons-in-table_promotion.svg"
              // @Renuka Wrong value at from -- content?.chnlCmpToolData?.elements?.commonData?.channelPromoLogoPath
            },
            gridInitViewMode: initViewMode,
            gridSortOptions: sortOptions,
            pkgIndicatorTooltipPrefixText: {
              included: pickChrome(globalLineupConfig?.commonData?.channelIncludedText, cct?.commonData?.channelIncludedText) ?? "Included",
              addon: pickChrome(globalLineupConfig?.commonData?.channelAddOntext, cct?.commonData?.channelAddOntext) ?? "Add-on channels",
              promo: pickChrome(globalLineupConfig?.commonData?.channelPromoText, cct?.commonData?.channelPromoText) ?? "Promo available"
            },
            /** NEW */ addOnPromoLogoInfo: {
              included: "Channel is included in package",
              addon:
                "Add-on channels (Channel can be added for an additional cost)",
              promo:
                "Promotions (Channel is included for an introductory time)"
            },
            // change structure to key value object to accommodate possible more ctas
            categoryFooterCtas: {
              MYHOMETEAM: content?.myHomeTeamModalCta
            },
            // @Renuka internet pdf link was hardcoded
            internetChnlsPDFLink: {
              ...variantInternetChnlsPDFLink,
              targeturl:
                "https://www.directv.com/dtvassets/sales/directv/upper_funnel/directv/channel-lineup/DIRECTV-via-Internet-ACQ-Packages-Channel-Lineup.pdf"
            },
            satelliteChnlsPDFLink: {
              ...variantSatelliteChnlsPDFLink,
              targeturl:
                variantSatelliteChnlsPDFLink?.targeturl ||
                "https://www.directv.com/dtvassets/sales/directv/upper_funnel/directv/channel-lineup/DIRECTV-via-Satellite-ACQ-Packages-Channel-Lineup.pdf"
            },
            satelliteCta: {
              label: variantSatelliteCta?.label,
              arialabel: variantSatelliteCta?.arialabel,
              url: variantSatelliteCta?.url
            },
            searchConfig: {
              inputLabel: cct?.commonData?.searchLabel,
              /** NEW */ searchIconSrc:
                "https://www.directv.com/dtvassets/global/icons/fusion/magnifying-glass_neutral500_filled.svg",
              /** NEW */ searchIconAlt: "Click to search for channels",
              /** NEW */ clearIconSrc:
                "https://www.directv.com/dtvassets/global/icons/fusion/close_blue900_filled.svg",
              clearIconAlt: "Clear search",
              // HARDCODED IN CURRENT -- cct?.commonData?.searchClearInputAlt,
              // current value: "Clear selection"
              multiSelectionOptionPrefix:
                cct?.commonData?.searchMultiSelectionGroupTitle,
              noMatchListMsg: "Hmm.. can't find that one. Try another search.",
              /** NEW */ noResultsMsgCategoriesBanner:
                "Channel is not available in Genre Packs",
              /** NEW */ noResultsMsgPackagesGrid:
                "Channel is not available in Signature Packages"
              // @Renuka HARDCODED IN CURRENT -- content?.chnlCmpToolData?.elements?.commonData?.searchNoMatchBodyMsg
              // @Renuka current CI value:  "Hmm.. can't find that one.",
              // @Renuka NOT USED -- content?.chnlCmpToolData?.elements?.commonData?.searchNoMatchInputMsg,
              // @Renuka NOT USED -- noMatchBodyMsgGP: "Channel is not available in Genre Packs.",
            },
            channelModals: {
              rsnTiers: content?.rsnTab?.rsnTiers,
              internetPkgCta: variantInternetPkgCta,
              satellitePkgCta: variantSatellitePkgCta,
              ctaForSatelliteLocalRSN: variantCtaForSatelliteLocalRSN?.[0],
              ctaForInternetInLocalRSN: variantCtaForInternetInLocalRSN?.[0],
              myFreeDTVPkgLabel: content?.myFreeDTVPkgLabel,
              myFreeDTVCTA: content?.myFreeDTVCTA?.[0],
              rsnModalLegal: content?.rsnTab?.rsnChnlLegal
            },
            embeddedSvods: embeddedSvods || [],
            isEmbeddedSvodsEnabled: isEmbeddedSvodsEnabled || false,
            genreNationals: genreNationals || {},
            rsnTierLegalTextObj: rsnTierLegalTextObj || {},
            // Field-tolerant MyFree wordmark: the SB story authors it on
            // `myFreeConfig.logo` (the comparison-grid header reads it there),
            // while AEM authors `packageData.icon` (what the genre-tab icon
            // banner renders). Prefer packageData.icon, fall back to logo so
            // both authoring shapes light up both consumers (same pattern as
            // parseThemeObject's background ?? backgroundColor).
            myFreeConfig: content?.myFreeConfig
              ? {
                ...content.myFreeConfig,
                packageData: {
                  ...content.myFreeConfig.packageData,
                  icon:
                      content.myFreeConfig.packageData?.icon ||
                      content.myFreeConfig.logo ||
                      ""
                }
              }
              : {},
            genreMapping: genreMapping || {},
            genreToPackageMapping: genreToPackageMapping || {}
          },
          searchObj: content?.searchObj,
          options: {
            id: componentId || ""
          },
          type: isSatelliteChlup ? ComponentNames.SATELLITE_CHANNEL_LISTING_PAGE : ComponentNames.CHANNEL_LISTING_PAGE

        },
        type: isSatelliteChlup ? ComponentNames.SATELLITE_CHANNEL_LISTING_PAGE : ComponentNames.CHANNEL_LISTING_PAGE

      };

      return fixedContent;
    }
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      componentId,
      index,
      isEmbeddedSvodsEnabled,
      enableGenrePackChannelView
    });

    return {
      props: {
        componentId: componentId || "",
        error: {
          message:
            "An error occurred while processing the channel listing page.",
          details: error instanceof Error ? error.message : String(error)
        }
      },
      type: ComponentNames.CHANNEL_LISTING_PAGE
    };
  }
};

export const filterChannels = (packages, channels, genrePackages?) => {
  try {
    const packageIdsArr = packages.map(pack => pack.packageId);
    packageIdsArr.push("ATT_TV_MYHOMETEAM");
    // Include genre package IDs so channels that only belong to genre packs
    // (e.g. C-SPAN 3 with ATT_TV_MYNEWS) are not filtered out before the
    // genre packs view can display them.
    if (genrePackages?.length) {
      genrePackages.forEach((gp: any) => {
        if (gp?.packageId && !packageIdsArr.includes(gp.packageId)) {
          packageIdsArr.push(gp.packageId);
        }
      });
    }
    const packageIds = [];
    for (const pkgIdStr of packageIdsArr) {
      const pkgArr = pkgIdStr.split("TV_");
      const pkgId = pkgArr[pkgArr.length - 1];
      packageIds.push(`ATT_TV_${pkgId}`);
      packageIds.push(`DTV_${pkgId}`);
    }

    const filteredChannels =
      channels &&
      channels.filter(c => {
        if (!c.IsActive || !packageIds?.length) {
          return false;
        }

        let chnlPackages = [
          "",
          ...(c.packageListSystem || []),
          ...(c.addonPackageList || []),
          ...(c.promoPackageList || [])
        ];

        // Remove excluded packages
        if (c?.excludedPackageList?.length) {
          chnlPackages = chnlPackages.filter(
            pkg => !c.excludedPackageList.includes(pkg)
          );
        }

        // If any packageId exists in chnlPackages, perform initial filters
        return packageIds.some(id => chnlPackages.includes(id));
      });
    return filteredChannels;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "filterChannels",
      packages: packages?.length,
      channels: channels?.length
    });
    return channels;
  }
};
